import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { MAP_DATA, LOCATIONS_BY_ID } from "./mapData";
import type { MapLocation } from "./mapData";
import { LocationModal } from "./LocationModal";
import { useGameStore } from "../../store/gameStore";
import { useAuth } from "../../contexts/AuthContext";
import { useMapRoom } from "../../hooks/useMapRoom";
import { Avatar } from "../Auth/AvatarPicker";
import type { RemotePlayer } from "../../hooks/useMapRoom";
import "./WorldMap.css";

// ─── SVG canvas dimensions ─────────────────────────────────────────────────────
const VIEW_W = 800;
const VIEW_H = 600;

// ─── Location icon paths (16×16, drawn relative to node center) ───────────────
const ICONS: Record<string, JSX.Element> = {
  siida: (
    // House / home
    <path
      d="M8 1 L15 7 L13 7 L13 15 L10 15 L10 10 L6 10 L6 15 L3 15 L3 7 L1 7 Z"
      fill="currentColor"
    />
  ),
  duottar: (
    // Mountain peaks
    <path
      d="M8 2 L14 14 H10 L8 10 L6 14 H2 Z M11 6 L15 14 H13 L11 10 Z"
      fill="currentColor"
    />
  ),
  johka: (
    // Water wave
    <>
      <path
        d="M1 8 C3 5 5 11 8 8 C11 5 13 11 15 8"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M1 12 C3 9 5 15 8 12 C11 9 13 15 15 12"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </>
  ),
  market: (
    // Tent / yoik
    <>
      <path d="M8 1 L15 13 H1 Z" fill="currentColor" />
      <rect x="6" y="10" width="4" height="3" fill="currentColor" />
    </>
  ),
};

// ─── Sub-components ────────────────────────────────────────────────────────────

function Road({
  from,
  to,
}: {
  from: MapLocation;
  to: MapLocation;
}) {
  const mx = (from.x + to.x) / 2;
  const my = (from.y + to.y) / 2 - 18; // slight arc offset
  return (
    <path
      d={`M ${from.x} ${from.y} Q ${mx} ${my} ${to.x} ${to.y}`}
      fill="none"
      stroke="#7a5a30"
      strokeWidth="3"
      strokeDasharray="6 5"
      strokeLinecap="round"
      opacity="0.65"
    />
  );
}

function LocationNode({
  location,
  isCurrent,
  onClick,
}: {
  location: MapLocation;
  isCurrent: boolean;
  onClick: () => void;
}) {
  const { unlocked, id, name, theme, x, y } = location;

  const fillColor = unlocked ? "#c8971a" : "#3a2c1e";
  const strokeColor = unlocked ? "#7a5a0e" : "#5a4a38";
  const iconColor = unlocked ? "#2a1a0a" : "#6b5a48";

  return (
    <g
      className={`map-node${unlocked ? "" : " map-node--locked"}`}
      onClick={unlocked ? onClick : undefined}
      role={unlocked ? "button" : undefined}
      aria-label={unlocked ? `Gå til ${name}` : `${name} (låst)`}
      tabIndex={unlocked ? 0 : undefined}
      onKeyDown={
        unlocked
          ? (e) => e.key === "Enter" && onClick()
          : undefined
      }
    >
      {/* Hover ring */}
      <circle
        cx={x}
        cy={y}
        r={38}
        fill="none"
        stroke="#c8971a"
        strokeWidth="1.5"
        opacity="0"
        className="map-node__ring"
      />

      {/* Current location indicator */}
      {isCurrent && (
        <circle
          cx={x}
          cy={y}
          r={34}
          fill="none"
          stroke="#e8c41a"
          strokeWidth="2"
          strokeDasharray="4 3"
          opacity="0.7"
        />
      )}

      {/* Locked hatch pattern fill */}
      {!unlocked && (
        <circle
          cx={x}
          cy={y}
          r={26}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth="2"
          opacity="0.6"
        />
      )}

      {/* Main node circle */}
      {unlocked && (
        <>
          {/* Glow layer */}
          <circle cx={x} cy={y} r={30} fill="#c8971a" opacity="0.12" />
          {/* Base circle */}
          <circle
            cx={x}
            cy={y}
            r={26}
            fill="url(#nodeGrad)"
            stroke={strokeColor}
            strokeWidth="2.5"
            className="map-node__circle"
          />
        </>
      )}

      {/* Theme icon */}
      <g
        transform={`translate(${x - 8}, ${y - 9})`}
        color={iconColor}
        style={{ opacity: unlocked ? 0.9 : 0.45 }}
        aria-hidden="true"
      >
        <svg viewBox="0 0 16 16" width="16" height="16" overflow="visible">
          {ICONS[id] ?? null}
        </svg>
      </g>

      {/* Lock icon for locked nodes */}
      {!unlocked && <LockIcon cx={x + 10} cy={y - 12} />}

      {/* Location name */}
      <text
        x={x}
        y={y + 40}
        className={`map-node__name${unlocked ? "" : " map-node__name--locked"}`}
      >
        {name}
      </text>

      {/* Theme subtitle */}
      <text
        x={x}
        y={y + 53}
        className={`map-node__theme${unlocked ? "" : " map-node__theme--locked"}`}
      >
        {theme}
      </text>
    </g>
  );
}

function LockIcon({ cx, cy }: { cx: number; cy: number }) {
  return (
    <g transform={`translate(${cx - 6}, ${cy - 7})`} aria-hidden="true">
      {/* Shackle */}
      <path
        d="M2 6 V4 A4 4 0 0 1 10 4 V6"
        fill="none"
        stroke="#8a7060"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      {/* Body */}
      <rect x="0" y="5" width="12" height="9" rx="2" fill="#5a4a38" stroke="#8a7060" strokeWidth="1" />
      {/* Keyhole */}
      <circle cx="6" cy="9" r="1.5" fill="#8a7060" />
      <rect x="5" y="9" width="2" height="3" rx="0.5" fill="#8a7060" />
    </g>
  );
}

function PlayerAvatar({ x, y }: { x: number; y: number }) {
  return (
    <g
      className="player-avatar__position"
      style={{ transform: `translate(${x}px, ${y}px)` }}
      aria-label="Din posisjon"
    >
      {/* Pulse ring (animated via CSS) */}
      <circle
        cx={0}
        cy={0}
        r={16}
        fill="none"
        stroke="#e05c2a"
        strokeWidth="2"
        opacity={0.7}
        className="player-avatar__pulse"
      />
      {/* Shadow */}
      <ellipse cx={0} cy={18} rx={10} ry={3} fill="#000" opacity={0.25} />
      {/* Body */}
      <g className="player-avatar__dot">
        <circle cx={0} cy={0} r={14} fill="#e05c2a" stroke="#f2ead8" strokeWidth="2.5" />
        {/* Simple person silhouette */}
        <circle cx={0} cy={-4} r={3.5} fill="#f2ead8" />
        <path
          d="M -5 8 Q 0 2 5 8"
          fill="#f2ead8"
          stroke="none"
        />
      </g>
    </g>
  );
}

// Badge icon lookup (mirrors workers/src/data/badges.ts)
const BADGE_ICONS: Record<string, string> = {
  mahtit: "📚", diedalas: "🎓", mahttalas: "⭐",
  duojars: "🔨", meaccar: "🏹",
  matkeolmmaj: "🗺️", mailmmeolmmaj: "🌍",
};

function OtherPlayerAvatar({
  player,
  offsetIndex,
}: {
  player: RemotePlayer;
  offsetIndex: number;
}) {
  const loc = LOCATIONS_BY_ID[player.locationId];
  if (!loc) return null;

  // Spread players around the location node
  const angle = (offsetIndex * 72 * Math.PI) / 180;
  const r = 44;
  const px = loc.x + Math.cos(angle) * r;
  const py = loc.y + Math.sin(angle) * r;

  const initial = player.username.charAt(0).toUpperCase();
  const topBadge = player.topBadges[0];

  return (
    <g className="other-player" aria-label={player.username}>
      {/* Pulse ring */}
      <circle
        cx={px}
        cy={py}
        r={12}
        fill="none"
        stroke="#4a9c3f"
        strokeWidth="1.5"
        className="other-player__pulse"
      />
      {/* Avatar circle */}
      <circle cx={px} cy={py} r={10} fill="#2a5c28" stroke="#a0e898" strokeWidth="1.5" />
      {/* Initial letter */}
      <text
        x={px}
        y={py + 4}
        className="other-player__initial"
        aria-hidden="true"
      >
        {initial}
      </text>
      {/* Top badge icon above avatar */}
      {topBadge && (
        <text
          x={px}
          y={py - 14}
          className="other-player__badge"
          aria-hidden="true"
        >
          {BADGE_ICONS[topBadge] ?? ""}
        </text>
      )}
      {/* Name label below */}
      <text x={px} y={py + 24} className="other-player__label">
        {player.username.slice(0, 8)}
      </text>
    </g>
  );
}

// ─── Compass rose (decorative) ────────────────────────────────────────────────

function CompassRose({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x}, ${y})`} opacity={0.5} aria-hidden="true">
      {/* N */}
      <path d="M0 -22 L4 -10 L0 -14 L-4 -10 Z" fill="#9c7a4a" />
      {/* S */}
      <path d="M0 22 L4 10 L0 14 L-4 10 Z" fill="#7a5a38" />
      {/* E */}
      <path d="M22 0 L10 4 L14 0 L10 -4 Z" fill="#7a5a38" />
      {/* W */}
      <path d="M-22 0 L-10 4 L-14 0 L-10 -4 Z" fill="#7a5a38" />
      <circle cx={0} cy={0} r={4} fill="#c8971a" />
      <circle cx={0} cy={0} r={2} fill="#2a1a0a" />
      <text x={0} y={-26} textAnchor="middle" fill="#c8971a" fontSize="8" fontWeight="700">N</text>
    </g>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

export function WorldMap() {
  const { currentLocation, setCurrentLocation, playerGold, playerBadges } = useGameStore();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [selectedLocation, setSelectedLocation] = useState<MapLocation | null>(null);

  // All players in the MapRoom shared Durable Object (includes self)
  const allPlayers = useMapRoom(token, user?.userId, currentLocation, playerBadges);

  // Exclude self, group by location for staggered rendering
  const otherPlayers = useMemo(
    () => allPlayers.filter((p) => p.userId !== user?.userId),
    [allPlayers, user?.userId]
  );

  const playersByLocation = useMemo(() => {
    const map = new Map<string, RemotePlayer[]>();
    for (const p of otherPlayers) {
      if (!map.has(p.locationId)) map.set(p.locationId, []);
      map.get(p.locationId)!.push(p);
    }
    return map;
  }, [otherPlayers]);

  const currentLocationData = LOCATIONS_BY_ID[currentLocation];

  function handleLocationClick(location: MapLocation) {
    setCurrentLocation(location.id);
    setSelectedLocation(location);
  }

  return (
    <div className="world-map">
      {/* ─── Top bar ──────────────────────────────────────────────────────────── */}
      <div className="world-map__topbar">
        <button
          className="world-map__avatar-btn"
          onClick={() => navigate("/profile")}
          aria-label="Profil"
        >
          <Avatar avatarId={user?.avatarId ?? "default"} size={34} />
        </button>
        <span className="world-map__topbar-name">{user?.username ?? ""}</span>
        <div className="world-map__hud">
          <span className="world-map__hud-coin">🪙</span>
          <span className="world-map__hud-gold">{playerGold.toLocaleString("nb")}</span>
        </div>
      </div>

      {/* ─── Bottom nav ───────────────────────────────────────────────────────── */}
      <nav className="world-map__nav" aria-label="Navigasjon">
        {(
          [
            { path: "/shop",        icon: "🛒", label: "Butikk"    },
            { path: "/market",      icon: "⚖️",  label: "Marked"    },
            { path: "/leaderboard", icon: "🏆", label: "Toppliste" },
            { path: "/profile",     icon: "👤", label: "Profil"    },
          ] as const
        ).map(({ path, icon, label }) => (
          <button
            key={path}
            className="world-map__nav-btn"
            onClick={() => navigate(path)}
            aria-label={label}
          >
            <span className="world-map__nav-icon">{icon}</span>
            <span className="world-map__nav-label">{label}</span>
          </button>
        ))}
      </nav>

      {/* ─── SVG Map ──────────────────────────────────────────────────────────── */}
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="xMidYMid meet"
        className="world-map__svg"
        role="img"
        aria-label="Verdenskart for Gollegiella"
      >
        <defs>
          {/* Parchment background gradient */}
          <radialGradient id="bgGrad" cx="45%" cy="50%" r="65%">
            <stop offset="0%" stopColor="#d8c490" />
            <stop offset="60%" stopColor="#c8ae78" />
            <stop offset="100%" stopColor="#a8885a" />
          </radialGradient>

          {/* Unlocked node gradient */}
          <radialGradient id="nodeGrad" cx="40%" cy="35%" r="65%">
            <stop offset="0%" stopColor="#e8b830" />
            <stop offset="100%" stopColor="#a87010" />
          </radialGradient>

          {/* Paper texture */}
          <filter id="paper" x="-5%" y="-5%" width="110%" height="110%">
            <feTurbulence
              type="turbulence"
              baseFrequency="0.65"
              numOctaves="3"
              seed="5"
              result="noise"
            />
            <feColorMatrix type="saturate" values="0" in="noise" result="grayNoise" />
            <feBlend in="SourceGraphic" in2="grayNoise" mode="multiply" result="blend" />
            <feComposite in="blend" in2="SourceGraphic" operator="in" />
          </filter>

          {/* Vignette */}
          <radialGradient id="vignette" cx="50%" cy="50%" r="70%">
            <stop offset="0%" stopColor="transparent" />
            <stop offset="100%" stopColor="rgba(10,6,2,0.55)" />
          </radialGradient>
        </defs>

        {/* Background parchment */}
        <rect
          x="0"
          y="0"
          width={VIEW_W}
          height={VIEW_H}
          fill="url(#bgGrad)"
          filter="url(#paper)"
        />

        {/* Vignette overlay */}
        <rect x="0" y="0" width={VIEW_W} height={VIEW_H} fill="url(#vignette)" />

        {/* Decorative outer border */}
        <rect
          x="14"
          y="14"
          width={VIEW_W - 28}
          height={VIEW_H - 28}
          fill="none"
          stroke="#9c7a4a"
          strokeWidth="1.5"
          opacity="0.6"
        />
        <rect
          x="20"
          y="20"
          width={VIEW_W - 40}
          height={VIEW_H - 40}
          fill="none"
          stroke="#9c7a4a"
          strokeWidth="0.5"
          opacity="0.35"
        />

        {/* Corner ornaments */}
        {([
          [24, 24], [VIEW_W - 24, 24],
          [24, VIEW_H - 24], [VIEW_W - 24, VIEW_H - 24],
        ] as [number, number][]).map(([cx, cy], i) => (
          <g key={i} transform={`translate(${cx}, ${cy})`} opacity={0.55}>
            <circle r={4} fill="none" stroke="#9c7a4a" strokeWidth="1" />
            <circle r={1.5} fill="#9c7a4a" />
          </g>
        ))}

        {/* Compass rose */}
        <CompassRose x={720} y={80} />

        {/* Title */}
        <text
          x="50%"
          y="42"
          textAnchor="middle"
          fill="#3a2810"
          fontSize="22"
          fontWeight="800"
          fontFamily="system-ui, sans-serif"
          letterSpacing="-0.02em"
          opacity="0.7"
        >
          Gollegiella
        </text>

        {/* ─── Roads ─────────────────────────────────────────────────────────── */}
        {MAP_DATA.roads.map((road) => {
          const from = LOCATIONS_BY_ID[road.from];
          const to = LOCATIONS_BY_ID[road.to];
          if (!from || !to) return null;
          return (
            <Road key={`${road.from}-${road.to}`} from={from} to={to} />
          );
        })}

        {/* ─── Location nodes ─────────────────────────────────────────────────── */}
        {MAP_DATA.locations.map((location) => (
          <LocationNode
            key={location.id}
            location={location}
            isCurrent={currentLocation === location.id}
            onClick={() => handleLocationClick(location)}
          />
        ))}

        {/* ─── Other players ──────────────────────────────────────────────────── */}
        {Array.from(playersByLocation.entries()).flatMap(([locId, players]) =>
          players.map((player, i) => (
            <OtherPlayerAvatar
              key={`${locId}-${player.userId}`}
              player={player}
              offsetIndex={i}
            />
          ))
        )}

        {/* ─── Current player avatar ──────────────────────────────────────────── */}
        {currentLocationData && (
          <PlayerAvatar x={currentLocationData.x} y={currentLocationData.y} />
        )}
      </svg>

      {/* ─── Location modal ─────────────────────────────────────────────────── */}
      {selectedLocation && (
        <LocationModal
          location={selectedLocation}
          onClose={() => setSelectedLocation(null)}
        />
      )}
    </div>
  );
}
